/**
 * "This element owns its own gestures — the table swipe must keep its hands off."
 *
 * SlideContainer has always had this idea, as a selector inlined in its
 * touchstart handler: a gesture beginning on the raise slider, a text field, or
 * anything tagged `data-swipe-ignore` is not a table swipe. It was only ever
 * applied on the TOUCH path, and nothing in the app actually wore the tag. This
 * module makes it the shared rule both gesture paths read, and gives panels a
 * prop to opt in with.
 *
 * THE BUG THAT MADE IT SHARED (EN-10653, desktop follow-up)
 * ---------------------------------------------------------
 * `useTrackpadTableSwipe` puts a GSAP Observer on the table surface with
 * `preventDefault: true`. That option is load-bearing — React's synthetic
 * `onWheel` is always passive, so attaching a non-passive listener to the node
 * directly is the only way to suppress Safari's two-finger history swipe-back.
 *
 * But it applies to EVERY event the Observer takes, anywhere in its target's
 * subtree. Chat and table history are `position: fixed`, which changes their
 * containing block but NOT their place in the DOM — they are still descendants
 * of the table surface, so their wheel events bubbled through it and were
 * cancelled before the browser could scroll them. Reproduced as: join a table,
 * build up some chat and history, open a second table as an observer (which is
 * what arms the gesture — `activeTables.length >= 2`), come back, and neither
 * panel scrolls with the mouse.
 *
 * The TOUCH half of this bug was fixed by not arming the Observer at all on
 * touch-only devices (SlideContainer's `useSupportsHover()` gate). That gate
 * cannot help here: a desktop mouse is precisely the input the trackpad path
 * exists to serve, so the Observer is supposed to be armed. The panels have to
 * be excluded by region instead — which is what this tag is for.
 *
 * WHY A TAG RATHER THAN A STYLE PROBE
 * -----------------------------------
 * Deciding "is this inside something scrollable?" by walking ancestors and
 * reading `getComputedStyle` / `scrollHeight` would force layout inside a wheel
 * handler — on the hot path of every scroll, in a client already being tuned for
 * frame spikes. One `closest()` call costs no layout, and it is greppable from
 * both ends.
 */

/** Marks an element (and its subtree) as opting out of the table swipe. */
export const SWIPE_IGNORE_ATTR = 'data-swipe-ignore';

/**
 * Everything the table swipe keeps away from.
 *
 * The controls are listed by shape rather than tagged individually because they
 * are third-party (MUI) or native, and are horizontally interactive in their own
 * right: dragging the raise slider or selecting text in a field must not page
 * the table out from under the player.
 */
export const SWIPE_IGNORE_SELECTOR = `.MuiSlider-root, input, textarea, [role="slider"], [${SWIPE_IGNORE_ATTR}]`;

/**
 * Spread onto a panel that scrolls itself:
 *
 *   <div className={styles.body} {...swipeIgnoreProps}>
 *
 * Prefer this to hand-writing the attribute so the two ends cannot drift.
 */
export const swipeIgnoreProps = { [SWIPE_IGNORE_ATTR]: '' } as const;

/**
 * Did this gesture start somewhere that owns itself?
 *
 * Defensive about the target: an event can be dispatched at the document, at a
 * text node, or at an element already detached by the time a handler runs, and
 * none of those have `closest`.
 */
export const isSwipeIgnoredTarget = (target: EventTarget | null | undefined): boolean => {
  if (!target || !(target instanceof Element)) return false;
  return !!target.closest(SWIPE_IGNORE_SELECTOR);
};
